import { randomBytes, randomInt, randomUUID } from 'crypto';
import express from 'express';
import { isIP } from 'net';
import { isSSRFSafeURL } from 'ssrfcheck';
import { Err, Ok, Result } from 'ts-results-es';
import { fromError } from 'zod-validation-error/v3';

import { getConfig } from '../../config.js';
import { log } from '../../logging/logger.js';
import { axios, AxiosResponse, getStringResponseHeader, isAxiosError } from '../../utils/axios.js';
import { milliseconds } from '../../utils/milliseconds.js';
import { parseUrl } from '../../utils/parseUrl.js';
import { retry } from '../../utils/retry.js';
import { setLongTimeout } from '../../utils/setLongTimeout.js';
import { checkRedirectUriAllowed } from './authorizeRedirectUri.js';
import { clientMetadataCache } from './clientMetadataCache.js';
import { getDeviceId, getDeviceName } from './device.js';
import { getDnsResolver } from './dnsResolver.js';
import { generateCodeChallenge } from './generateCodeChallenge.js';
import { isValidRedirectUri } from './isValidRedirectUri.js';
import { matchesRegisteredRedirectUri } from './matchesRegisteredRedirectUri.js';
import { TABLEAU_CLOUD_SERVER_URL } from './provider.js';
import { cimdMetadataSchema, ClientMetadata, mcpAuthorizeSchema } from './schemas.js';
import { getSupportedScopes, parseScopes, validateScopes } from './scopes.js';
import { ClientRegistration, PendingAuthorization } from './types.js';

/**
 * OAuth 2.1 Authorization Endpoint
 *
 * Handles authorization requests with PKCE parameters.
 * Validates request, stores pending authorization, and
 * redirects to Tableau OAuth.
 */
export function authorize(
  app: express.Application,
  pendingAuthorizations: Map<string, PendingAuthorization>,
  clientRegistrations: Map<string, ClientRegistration>,
): void {
  const config = getConfig();

  app.get('/oauth2/authorize', async (req, res) => {
    const result = mcpAuthorizeSchema.safeParse(req.query);

    if (!result.success) {
      res.status(400).json({
        error: 'invalid_request',
        error_description: fromError(result.error).toString(),
      });
      return;
    }

    const {
      client_id,
      redirect_uri,
      response_type,
      code_challenge,
      code_challenge_method,
      state,
      scope,
    } = result.data;

    let clientName: string | undefined;
    const clientIdUrl = parseUrl(client_id);
    if (clientIdUrl) {
      // Client ID is a URL, so we need to attempt to fetch the client metadata from the URL
      const clientResult = await getClientFromMetadataDoc(clientIdUrl);
      if (clientResult.isErr()) {
        res.status(400).json(clientResult.error);
        return;
      }

      const { redirect_uris, response_types, client_name } = clientResult.value;
      clientName = client_name;

      if (response_types && !response_types.find((type) => type === response_type)) {
        res.status(400).json({
          error: 'unsupported_response_type',
          error_description: `Unsupported response type: ${response_type}`,
        });
        return;
      }

      if (
        redirect_uris &&
        !redirect_uris.some((uri) => matchesRegisteredRedirectUri(redirect_uri, uri))
      ) {
        res.status(400).json({
          error: 'invalid_request',
          error_description: `Invalid redirect URI: ${redirect_uri}`,
        });
        return;
      }
    }

    if (response_type !== 'code') {
      res.status(400).json({
        error: 'unsupported_response_type',
        error_description: 'Only authorization code flow is supported',
      });
      return;
    }

    if (code_challenge_method !== 'S256') {
      res.status(400).json({
        error: 'invalid_request',
        error_description: 'Only S256 code challenge method is supported',
      });
      return;
    }

    if (!isValidRedirectUri(redirect_uri)) {
      res.status(400).json({
        error: 'invalid_request',
        error_description: `Invalid redirect URI: ${redirect_uri}`,
      });
      return;
    }

    const { enforceScopes, advertiseApiScopes } = config.oauth;
    const requestedScopes = parseScopes(scope);
    const { valid: validScopes, invalid: invalidScopes } = validateScopes(
      requestedScopes,
      await getSupportedScopes({ includeApiScopes: advertiseApiScopes, clientId: client_id }),
    );

    if (invalidScopes.length > 0) {
      res.status(400).json({
        error: 'invalid_scope',
        error_description: `Unsupported scopes: ${invalidScopes.join(', ')}`,
      });
      return;
    }

    const scopesToGrant =
      validScopes.length > 0
        ? validScopes
        : enforceScopes
          ? await getSupportedScopes({ includeApiScopes: advertiseApiScopes, clientId: client_id })
          : [];

    // Redirect URI security enforcement for opaque client_ids (runs after baseline param
    // validation, before storing pending auth). The CIMD path enforces its own allowlist above.
    if (!clientIdUrl) {
      const redirectError = checkRedirectUriAllowed({
        clientId: client_id,
        redirectUri: redirect_uri,
        clientRegistrations,
      });
      if (redirectError) {
        res.status(400).json(redirectError);
        return;
      }
    }

    // Generate Tableau state and store pending authorization
    const tableauState = randomBytes(32).toString('hex');
    const authKey = randomBytes(32).toString('hex');

    const tableauClientId = randomUUID();
    // 22-64 bytes (44-128 chars) is the recommended length for code verifiers
    const numCodeVerifierBytes = randomInt(22, 65);
    const tableauCodeVerifier = randomBytes(numCodeVerifierBytes).toString('hex');
    const tableauCodeChallenge = generateCodeChallenge(tableauCodeVerifier);
    pendingAuthorizations.set(authKey, {
      clientId: client_id,
      redirectUri: redirect_uri,
      codeChallenge: code_challenge,
      state: state ?? '',
      tableauState,
      tableauClientId,
      tableauCodeVerifier,
      scopes: scopesToGrant,
    });

    // Clean up expired authorizations
    setLongTimeout(() => pendingAuthorizations.delete(authKey), config.oauth.authzCodeTimeoutMs);

    // Redirect to Tableau OAuth
    const server = config.server || TABLEAU_CLOUD_SERVER_URL;
    const oauthUrl = new URL(`${server}/oauth2/v1/auth`);
    oauthUrl.searchParams.set('client_id', tableauClientId);
    oauthUrl.searchParams.set('code_challenge', tableauCodeChallenge);
    oauthUrl.searchParams.set('code_challenge_method', 'S256');
    oauthUrl.searchParams.set('response_type', 'code');
    oauthUrl.searchParams.set('redirect_uri', config.oauth.redirectUri);
    oauthUrl.searchParams.set('state', `${authKey}:${tableauState}`);
    const deviceName = getDeviceName(redirect_uri, state ?? '', clientName);
    oauthUrl.searchParams.set('device_id', getDeviceId(deviceName));
    oauthUrl.searchParams.set('target_site', config.siteName);
    oauthUrl.searchParams.set('device_name', deviceName);
    oauthUrl.searchParams.set('client_type', 'tableau-mcp');

    if (config.oauth.lockSite) {
      // The "redirected" parameter is used by Tableau's OAuth controller to determine whether the user will be shown the site picker.
      // When provided, the user will not be shown the site picker.
      oauthUrl.searchParams.set('redirected', 'true');
    }

    const redirectUrl = await getOAuthRedirectUrl(oauthUrl, { lockSite: config.oauth.lockSite });
    res.redirect(redirectUrl.toString());
  });
}

async function getOAuthRedirectUrl(
  initialOAuthUrl: URL,
  { lockSite }: { lockSite: boolean },
): Promise<URL> {
  if (lockSite) {
    // When the site is locked, Tableau does the right thing and never shows the site picker,
    // regardless of whether the user already has an active Tableau session in their browser.
    return initialOAuthUrl;
  }

  // When the site is not locked, Tableau does the right thing and shows the site picker, but only on Cloud.
  // On Server, if the user does not have an active Tableau session in their browser,
  // Tableau does not show the site picker.
  // We can force it to by changing the path from #/signin to #/site.

  try {
    const response = await fetch(initialOAuthUrl, { redirect: 'manual' });
    if (response.status === 302) {
      // The response is a redirect to the Tableau OAuth login page.
      // Force it to ultimately show the site picker by changing the path from #/signin to #/site.
      const location = response.headers.get('location');
      if (location?.startsWith('#/signin') || location?.startsWith('/#/signin')) {
        const locationUrl = new URL(location.replace('#/signin', '#/site'), initialOAuthUrl.origin);
        return locationUrl;
      }
    }
  } catch (error) {
    log({
      message: 'Failed to follow Tableau OAuth redirect for site picker',
      level: 'error',
      logger: 'oauth',
      data: error,
    });
    return initialOAuthUrl;
  }

  return initialOAuthUrl;
}

// https://client.dev/servers
async function getClientFromMetadataDoc(
  clientMetadataUrl: URL,
): Promise<Result<ClientMetadata, { error: string; error_description: string }>> {
  const originalUrl = clientMetadataUrl.toString();
  const cache = clientMetadataCache.get(originalUrl);
  if (cache) {
    return Ok(cache);
  }

  const originalHostname = clientMetadataUrl.hostname;
  if (!isIP(clientMetadataUrl.hostname)) {
    try {
      // Resolve the IP from DNS
      const dnsResolver = getDnsResolver();
      const resolvedIps = await dnsResolver.resolve4(clientMetadataUrl.hostname);
      let ipAddress = resolvedIps.find(Boolean);
      if (!ipAddress) {
        const resolvedIps = await dnsResolver.resolve6(clientMetadataUrl.hostname);
        ipAddress = resolvedIps.find(Boolean);
        if (!ipAddress) {
          return Err({
            error: 'invalid_request',
            error_description: 'IP address of Client Metadata URL could not be resolved',
          });
        }
      }
      // Replace the hostname with the resolved IP Address
      clientMetadataUrl.hostname = ipAddress;
    } catch (error) {
      log({
        message: `DNS resolution failed for client metadata URL ${clientMetadataUrl.hostname}`,
        level: 'error',
        logger: 'oauth',
        data: error,
      });
      return Err({
        error: 'invalid_request',
        error_description: 'IP address of Client Metadata URL could not be resolved',
      });
    }
  }

  const isSafe = isSSRFSafeURL(clientMetadataUrl.toString(), {
    allowedProtocols: ['https'],
    autoPrependProtocol: false,
  });

  if (!isSafe) {
    return Err({
      error: 'invalid_request',
      error_description: 'Client Metadata URL is not allowed',
    });
  }

  let response: AxiosResponse;
  try {
    const client = axios.create();
    response = await retry(
      () =>
        client.get(clientMetadataUrl.toString(), {
          timeout: 5000,
          maxContentLength: 5 * 1024, // 5 KB
          maxRedirects: 3,
          headers: {
            Accept: 'application/json',
            Host: originalHostname,
          },
        }),
      {
        retryIf: (error) => {
          if (!isAxiosError(error)) {
            return true;
          }

          const status = error.response?.status;
          if (status) {
            return status >= 500 && status < 600;
          }

          return true;
        },
      },
    );
  } catch (error) {
    log({
      message: `Failed to fetch client metadata from ${originalUrl}`,
      level: 'error',
      logger: 'oauth',
      data: error,
    });
    return Err({
      error: 'invalid_request',
      error_description: 'Unable to fetch client metadata',
    });
  }

  const contentType = getStringResponseHeader(response.headers, 'content-type');
  if (!contentType) {
    return Err({
      error: 'invalid_client_metadata',
      error_description: 'Client Metadata URL must return a valid Content-Type header',
    });
  }

  const contentTypes = contentType.split(';').map((s) => s.trim());
  if (!contentTypes.includes('application/json')) {
    return Err({
      error: 'invalid_client_metadata',
      error_description: 'Client Metadata URL must return a JSON response',
    });
  }

  const clientMetadataResult = cimdMetadataSchema.safeParse(response.data);
  if (!clientMetadataResult.success) {
    return Err({
      error: 'invalid_client_metadata',
      error_description: `Client metadata is invalid: ${fromError(clientMetadataResult.error).toString()}`,
    });
  }

  if (clientMetadataResult.data.client_id !== originalUrl) {
    return Err({
      error: 'invalid_client_metadata',
      error_description: 'Client ID mismatch',
    });
  }

  const cacheControl = getStringResponseHeader(response.headers, 'cache-control');
  let cacheControlMaxAge: string | undefined;
  if (cacheControl) {
    const maxAgeDirective = cacheControl
      .split(',')
      .map((s) => s.trim())
      .find((s) => /^max-age\s*=\s*\d+$/i.test(s));

    if (maxAgeDirective) {
      cacheControlMaxAge = maxAgeDirective.split('=')[1].trim();
    }
  }

  let cacheExpiryMs = clientMetadataCache.defaultExpirationTimeMs;
  if (cacheControlMaxAge) {
    const cacheControlMaxAgeSeconds = parseInt(cacheControlMaxAge);
    if (!isNaN(cacheControlMaxAgeSeconds) && cacheControlMaxAgeSeconds >= 0) {
      cacheExpiryMs = Math.min(
        milliseconds.fromDays(1),
        milliseconds.fromSeconds(cacheControlMaxAgeSeconds),
      );
    }
  }

  if (cacheExpiryMs > 0) {
    clientMetadataCache.set(originalUrl, clientMetadataResult.data, cacheExpiryMs);
  }

  return Ok(clientMetadataResult.data);
}
